import React, { useCallback, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import SendIcon from '@mui/icons-material/Send'
import { oportunidadeService } from '@/services/oportunidadeService'
import { EncaminhamentoParceiro, StatusEncaminhamento } from '@/types/oportunidade'
import { formatDate } from '@/utils/formatters'
import { elegibilidadeReservaService } from '@/services/elegibilidadeReservaService'
import { impedimentoDoErro, mensagemErroElegibilidade } from '@/utils/errosElegibilidade'
import { ImpedimentoEncaminhamento } from '@/types/elegibilidadeReserva'
import { ROTULO_RESULTADO_ELEGIBILIDADE, TAMANHO_MAXIMO_MOTIVO_ELEGIBILIDADE } from '@/types/elegibilidade'
import { itensBloqueantesDoErro } from '@/services/checklistReservaService'
import { AlertaItensBloqueantes } from '@/components/documentos/ItemChecklistInfo'
import type { ItemBloqueanteChecklist } from '@/types/checklist'

/**
 * Encaminhamento formal da reserva ao parceiro selecionado (Épico 4) — back-office.
 *
 * O botão só aparece quando encaminhar é possível E útil: sem histórico, ou após uma
 * recusa ("Encaminhar novamente", que gera versão nova — nada é sobrescrito). Com um
 * pacote em voo (ENVIADO) o backend bloquearia o reenvio; o card explica em vez de
 * oferecer um botão que falha. Após o aceite o ciclo está fechado — reenviar por cima
 * de um aceite é decisão rara o bastante para não merecer botão.
 *
 * P2 (AN-PP2 §6, Q66): o servidor pode recusar com 422 `ELEGIBILIDADE_IMPEDE_ENCAMINHAMENTO`.
 * O diálogo passa a mostrar o resumo (explicações do motor) e, SÓ para o ADMIN, o motivo e
 * "Sobrepor e encaminhar" — duas chamadas: sobreposição (com o `politicaId`/`hashEntradas` do
 * 422, o que o ADMIN viu) e o encaminhamento. Nada é decidido na tela.
 */

interface Props {
  oportunidadeId: string
  /** Nome do parceiro selecionado — destinatário do pacote, mostrado na confirmação. */
  parceiroNome?: string | null
  /** Encaminhar é ADMIN (quem seleciona, encaminha) — espelha o @PreAuthorize do endpoint. */
  podeEncaminhar: boolean
  /** P2: sobrepor o impedimento de elegibilidade é só ADMIN (Q67, Q-PP2-7). */
  podeSobrepor?: boolean
  /** P2: avisa a página que a elegibilidade mudou (sobreposição gravada). */
  onElegibilidadeAlterada?: () => void
}

/** Mensagem legível de qualquer falha (o interceptor rejeita com objeto, não `Error`). */
const mensagemDe = (e: unknown, fallback: string): string => {
  const m = (e as { message?: unknown } | null)?.message
  return typeof m === 'string' && m ? m : fallback
}

const statusChip: Record<StatusEncaminhamento, { label: string; color: 'warning' | 'success' | 'error' }> = {
  ENVIADO: { label: 'Aguardando retorno', color: 'warning' },
  ACEITO: { label: 'Aceito pelo parceiro', color: 'success' },
  RECUSADO: { label: 'Recusado pelo parceiro', color: 'error' },
}

const EncaminhamentoParceiroCard: React.FC<Props> = ({
  oportunidadeId, parceiroNome, podeEncaminhar, podeSobrepor = false, onElegibilidadeAlterada,
}) => {
  const [encaminhamentos, setEncaminhamentos] = useState<EncaminhamentoParceiro[] | null>(null)
  const [carregando, setCarregando] = useState(true)
  const [erro, setErro] = useState<string | null>(null)
  const [confirmando, setConfirmando] = useState(false)
  const [enviando, setEnviando] = useState(false)
  /** P2: 422 `ELEGIBILIDADE_IMPEDE_ENCAMINHAMENTO` — resumo para o diálogo de sobreposição. */
  const [impedimento, setImpedimento] = useState<ImpedimentoEncaminhamento | null>(null)
  const [motivoSobreposicao, setMotivoSobreposicao] = useState('')
  const [erroSobreposicao, setErroSobreposicao] = useState<string | null>(null)
  const [itensBloqueantes, setItensBloqueantes] = useState<ItemBloqueanteChecklist[]>([])

  const carregar = useCallback(async () => {
    setCarregando(true)
    setErro(null)
    try {
      setEncaminhamentos(await oportunidadeService.listarEncaminhamentos(oportunidadeId))
    } catch {
      setErro('Não foi possível carregar os encaminhamentos.')
    } finally {
      setCarregando(false)
    }
  }, [oportunidadeId])

  useEffect(() => {
    void carregar()
  }, [carregar])

  const fecharDialogo = () => {
    setConfirmando(false)
    setImpedimento(null)
    setMotivoSobreposicao('')
    setErroSobreposicao(null)
  }

  const encaminhar = async () => {
    setEnviando(true)
    setErro(null)
    try {
      const novo = await oportunidadeService.encaminharParceiro(oportunidadeId)
      // Mais recente primeiro — mesma ordem do GET (versão desc).
      setEncaminhamentos((prev) => [novo, ...(prev ?? [])])
      fecharDialogo()
    } catch (e) {
      const imp = impedimentoDoErro(e)
      if (imp) {
        // Nada foi gravado no servidor (rollback); o diálogo vira o resumo do impedimento.
        setImpedimento(imp)
        return
      }
      const itens = itensBloqueantesDoErro(e)
      setItensBloqueantes(itens)
      // Bloqueio do checklist: fecha a confirmação para a lista de itens ficar visível no cartão.
      if (itens.length > 0) fecharDialogo()
      setErro(mensagemDe(e, 'Não foi possível encaminhar a reserva.'))
    } finally {
      setEnviando(false)
    }
  }

  /** ADMIN: sobrepõe exatamente o que viu (politicaId/hashEntradas do 422) e encaminha. */
  const sobreporEEncaminhar = async () => {
    if (!impedimento || !motivoSobreposicao.trim()) return
    setEnviando(true)
    setErroSobreposicao(null)
    try {
      await elegibilidadeReservaService.sobrepor(oportunidadeId, {
        motivo: motivoSobreposicao.trim(),
        politicaId: impedimento.politicaId ?? null,
        hashEntradas: impedimento.hashEntradas ?? null,
      })
      onElegibilidadeAlterada?.()
    } catch (e) {
      setErroSobreposicao(mensagemErroElegibilidade(e, 'Não foi possível sobrepor o impedimento.'))
      setEnviando(false)
      return
    }
    setEnviando(false)
    await encaminhar()
  }

  const ultimo = encaminhamentos?.[0] ?? null
  const podeEnviarAgora = podeEncaminhar && (ultimo === null || ultimo.status === 'RECUSADO')

  return (
    <Card sx={{ mb: 3 }}>
      <CardHeader
        title="Encaminhamento ao parceiro"
        subheader={ultimo ? `Protocolo ${ultimo.protocolo}` : undefined}
      />
      <CardContent>
        {carregando ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
            <CircularProgress size={24} aria-label="Carregando encaminhamentos" />
          </Box>
        ) : (
          <Stack spacing={1.5}>
            {erro &&
              (itensBloqueantes.length > 0 ? (
                // P3: 422 CHECKLIST_NAO_VALIDADO — itens ainda não validados (VENCIDO incluso).
                <AlertaItensBloqueantes mensagem={erro} itens={itensBloqueantes} />
              ) : (
                <Alert severity="error">{erro}</Alert>
              ))}

            {ultimo === null ? (
              <Typography variant="body2" color="textSecondary">
                A reserva ainda não foi encaminhada ao parceiro.
              </Typography>
            ) : (
              <Box>
                <Stack direction="row" spacing={1} alignItems="center">
                  <Chip
                    size="small"
                    label={statusChip[ultimo.status].label}
                    color={statusChip[ultimo.status].color}
                  />
                  <Typography variant="caption" color="textSecondary">
                    v{ultimo.versao} · enviado em {formatDate(ultimo.enviadoEm)}
                  </Typography>
                </Stack>
                {ultimo.status !== 'ENVIADO' && ultimo.retornoEm && (
                  <Typography variant="caption" color="textSecondary" display="block" sx={{ mt: 0.5 }}>
                    retorno em {formatDate(ultimo.retornoEm)}
                  </Typography>
                )}
                {ultimo.retornoMotivo && (
                  <Typography variant="body2" sx={{ mt: 0.5 }}>
                    Parecer do parceiro: {ultimo.retornoMotivo}
                  </Typography>
                )}
              </Box>
            )}

            {ultimo?.status === 'ENVIADO' && (
              <Typography variant="caption" color="textSecondary">
                Há um pacote aguardando retorno — um novo envio só é possível após a
                resposta do parceiro.
              </Typography>
            )}

            {podeEnviarAgora && (
              <Button
                variant="contained"
                size="small"
                startIcon={<SendIcon />}
                onClick={() => setConfirmando(true)}
              >
                {ultimo?.status === 'RECUSADO' ? 'Encaminhar novamente' : 'Encaminhar ao parceiro'}
              </Button>
            )}

            {encaminhamentos && encaminhamentos.length > 1 && (
              <>
                <Divider />
                <Box>
                  <Typography variant="caption" color="textSecondary">
                    Envios anteriores
                  </Typography>
                  {encaminhamentos.slice(1).map((e) => (
                    <Typography key={e.id} variant="caption" display="block" color="textSecondary">
                      {e.protocolo} · {statusChip[e.status].label.toLowerCase()} ·{' '}
                      {formatDate(e.enviadoEm)}
                    </Typography>
                  ))}
                </Box>
              </>
            )}
          </Stack>
        )}
      </CardContent>

      <Dialog open={confirmando} onClose={() => !enviando && fecharDialogo()} maxWidth={impedimento ? 'sm' : 'xs'} fullWidth>
        <DialogTitle>{impedimento ? 'Elegibilidade impede o encaminhamento' : 'Encaminhar ao parceiro'}</DialogTitle>
        {impedimento ? (
          <DialogContent>
            <Alert severity="warning" sx={{ mb: 2 }} data-testid="impedimento-elegibilidade">
              Com a política vigente, a elegibilidade
              {impedimento.resultado ? ` (${ROTULO_RESULTADO_ELEGIBILIDADE[impedimento.resultado]})` : ''} impede o
              encaminhamento ao parceiro. Nada foi enviado.
              {impedimento.explicacoes.map((x, i) => (
                <Typography key={i} variant="body2">
                  • {x.mensagem}
                </Typography>
              ))}
            </Alert>
            {podeSobrepor ? (
              <Stack spacing={1.5}>
                <Typography variant="body2">
                  Ajuste os dados declarados da reserva ou sobreponha o impedimento. A sobreposição vale para estes
                  dados e esta política; se mudarem, ela deixa de valer.
                </Typography>
                <TextField
                  label="Motivo da sobreposição"
                  value={motivoSobreposicao}
                  onChange={(e) => setMotivoSobreposicao(e.target.value)}
                  required
                  fullWidth
                  multiline
                  minRows={2}
                  disabled={enviando}
                  inputProps={{ maxLength: TAMANHO_MAXIMO_MOTIVO_ELEGIBILIDADE }}
                  helperText="Obrigatório — fica na linha do tempo, na auditoria e no pacote enviado."
                />
                {erroSobreposicao && <Alert severity="error">{erroSobreposicao}</Alert>}
              </Stack>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Ajuste os dados declarados da reserva. Só o Administrador pode sobrepor o impedimento.
              </Typography>
            )}
          </DialogContent>
        ) : (
        <DialogContent>
          <Typography variant="body2">
            A reserva será encaminhada a <strong>{parceiroNome ?? 'parceiro selecionado'}</strong>.
          </Typography>
          <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: 'block' }}>
            O envio gera um pacote imutável (com protocolo e hash) com os dados atuais da
            reserva — alterações posteriores não mudam o que foi enviado. Um novo envio só
            será possível após o retorno do parceiro.
          </Typography>
        </DialogContent>
        )}
        <DialogActions>
          <Button onClick={fecharDialogo} disabled={enviando}>
            Cancelar
          </Button>
          {impedimento ? (
            podeSobrepor && (
              <Button
                variant="contained"
                color="warning"
                onClick={() => void sobreporEEncaminhar()}
                disabled={enviando || !motivoSobreposicao.trim()}
              >
                {enviando ? 'Encaminhando…' : 'Sobrepor e encaminhar'}
              </Button>
            )
          ) : (
            <Button variant="contained" onClick={() => void encaminhar()} disabled={enviando}>
              {enviando ? 'Encaminhando…' : 'Confirmar envio'}
            </Button>
          )}
        </DialogActions>
      </Dialog>
    </Card>
  )
}

export default EncaminhamentoParceiroCard
